// ============================================================
// 휴대폰 연락처 가져오기 (Contact Picker API)
//
// 브라우저가 제공하는 navigator.contacts 를 사용한다.
// 사용자가 폰의 연락처 선택 창에서 직접 고른 사람만 넘어오며,
// 웹페이지가 연락처 전체를 몰래 읽는 것은 불가능하다(브라우저가 차단).
//
// 지원 환경:
//   ○ 안드로이드 Chrome (HTTPS 필수)
//   × 삼성 인터넷, iOS Safari, PC 브라우저
//
// 사용처: components/ReceiverManageModal.jsx (SMS 수신처 등록)
// ============================================================

/** 이 기기·브라우저에서 연락처 가져오기가 가능한가 */
export function canPickContacts() {
  return typeof navigator !== "undefined"
      && "contacts" in navigator
      && "ContactsManager" in window;
}

/**
 * 전화번호를 010-1234-5678 형태로 정리한다.
 * 폰 연락처에는 형식이 제각각으로 저장되어 있어 그대로 쓰면 문자 발송이 실패한다.
 *   "+82 10-1234-5678" → "010-1234-5678"
 *   "01012345678"      → "010-1234-5678"
 *   "02)878-5989"      → "02-878-5989"
 */
export function normalizePhone(raw) {
  if (!raw) return "";
  let d = String(raw).replace(/\D/g, "");   // 숫자만 남김

  // 국제형식 +82 → 국내형식 0 으로
  if (d.startsWith("82")) d = "0" + d.slice(2);

  if (!d) return "";

  // 서울 02 는 국번이 2자리라 따로 처리
  if (d.startsWith("02")) {
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0, 2)}-${d.slice(2)}`;
    return `${d.slice(0, 2)}-${d.slice(2, d.length - 4)}-${d.slice(-4)}`;
  }

  if (d.length <= 3) return d;
  if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, d.length - 4)}-${d.slice(-4)}`;
}

/**
 * 연락처 선택 창을 띄운다.
 * @param {boolean} multiple  여러 명 선택 허용 여부
 * @returns {Promise<Array<{name, phone, allPhones}>>}
 *          취소했거나 지원하지 않으면 빈 배열
 */
export async function pickContacts(multiple = false) {
  if (!canPickContacts()) return [];
  try {
    // 이름과 전화번호만 요청한다 (필요 최소한만 받는 것이 원칙)
    const picked = await navigator.contacts.select(["name", "tel"], { multiple });
    return (picked || []).map((c) => ({
      // name/tel 은 배열로 온다 (한 사람이 번호를 여러 개 가질 수 있으므로)
      name: Array.isArray(c.name) ? (c.name[0] || "") : (c.name || ""),
      phone: normalizePhone(Array.isArray(c.tel) ? (c.tel[0] || "") : (c.tel || "")),
      // 번호가 여러 개인 경우를 대비해 전부 보관 (B단계에서 활용 예정)
      allPhones: (Array.isArray(c.tel) ? c.tel : [c.tel]).filter(Boolean).map(normalizePhone),
    })).filter((c) => c.name || c.phone);
  } catch (e) {
    // 사용자가 취소를 누르면 예외가 발생한다. 오류가 아니므로 조용히 넘어간다.
    return [];
  }
}
